import { useEffect, useState } from "react";
import { Star, Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import SectionHeading from "./SectionHeading";
import { Reveal } from "./motion/Reveal";

const FALLBACK_TESTIMONIALS = [
  {
    id: 1,
    client_name: "Marcus Vance",
    client_role: "Head of Equipment Procurement",
    company: "Apex Court & Racket Co.",
    country: "United States",
    country_flag: "🇺🇸",
    rating: 5,
    quote: "Pickleballgearz's cold-press thermoformed carbon paddles passed our lab deflection tests on the first batch. Their MOQ flexibility allowed us to launch a 3-model retail line in under 60 days.",
    avatar: null,
  },
  {
    id: 2,
    client_name: "Elena Rostova",
    client_role: "Brand Director",
    company: "Vanguard Paddle Sports",
    country: "Canada",
    country_flag: "🇨🇦",
    rating: 5,
    quote: "Working directly with their Sialkot engineering team cut our lead times in half. Full Pantone edge matching and custom retail box packaging were executed flawlessly.",
    avatar: null,
  },
  {
    id: 3,
    client_name: "Tobias Richter",
    client_role: "Founder & CEO",
    company: "PaceBall Athletic Gear",
    country: "Germany",
    country_flag: "🇩🇪",
    rating: 5,
    quote: "The durability of their 40-hole tournament balls is the highest we've tested against cold-weather cracking. An indispensable OEM partner for our European distribution network.",
    avatar: null,
  },
];

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setTestimonials(r.data);
        } else {
          setTestimonials(FALLBACK_TESTIMONIALS);
        }
      })
      .catch(() => {
        setTestimonials(FALLBACK_TESTIMONIALS);
      });
  }, []);

  const list = testimonials.length > 0 ? testimonials : FALLBACK_TESTIMONIALS;
  const current = list[activeIdx] || list[0];

  const prev = () => setActiveIdx((i) => (i === 0 ? list.length - 1 : i - 1));
  const next = () => setActiveIdx((i) => (i === list.length - 1 ? 0 : i + 1));

  return (
    <section className="border-t border-black/10 bg-brand-smoke py-24 md:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            number="06"
            title="Verified Client Reviews"
            sub="Trusted by commercial brands, tournaments, and sports distributors worldwide."
            className="!mb-0"
          />
          <div className="flex gap-2">
            <button
              onClick={prev}
              aria-label="Previous review"
              data-testid="testimonial-prev"
              className="flex h-11 w-11 items-center justify-center border border-black/20 bg-white text-brand-ink transition-colors hover:border-brand-ink hover:bg-brand-ink hover:text-white"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              onClick={next}
              aria-label="Next review"
              data-testid="testimonial-next"
              className="flex h-11 w-11 items-center justify-center border border-black/20 bg-white text-brand-ink transition-colors hover:border-brand-ink hover:bg-brand-ink hover:text-white"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <div className="mt-14 border border-black/10 bg-white p-8 md:p-14">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <Quote className="text-brand-red opacity-30 mb-6" size={42} />
              <p className="font-display text-2xl font-normal leading-relaxed text-brand-ink sm:text-3xl md:text-4xl">
                “{current.quote}”
              </p>
              <div className="mt-8 flex items-center gap-1">
                {[...Array(current.rating || 5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-brand-red text-brand-red" />
                ))}
              </div>
            </div>

            <div className="border-t border-black/10 pt-8 lg:col-span-4 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
              <div className="flex items-center gap-4">
                {current.avatar ? (
                  <img
                    src={current.avatar}
                    alt={current.client_name}
                    className="h-14 w-14 rounded-full border border-black/10 object-cover"
                  />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center bg-brand-ink font-display text-lg font-semibold text-white">
                    {current.client_name?.charAt(0) || "C"}
                  </div>
                )}
                <div>
                  <h4 className="font-display text-xl font-medium tracking-tight text-brand-ink">
                    {current.client_name}
                  </h4>
                  <p className="font-mono text-xs uppercase tracking-wider text-neutral-400">
                    {current.client_role}
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-brand-red">
                    {current.company} {current.country_flag && <span className="ml-1">{current.country_flag}</span>}
                  </p>
                </div>
              </div>

              <div className="mt-8 flex gap-2">
                {list.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`h-1.5 transition-all ${
                      activeIdx === i ? "w-8 bg-brand-red" : "w-3 bg-black/15 hover:bg-black/30"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
